import { useCallback, useEffect, useRef, useState } from "react"
import { useConfigFlag } from "@cloudbreakus/featurebang-react"
import { SSE } from "utils/SSE"
import { useAuth } from "contexts/Auth"
import { useCall } from "contexts/Call"
import { useProfile } from "contexts/Profile"
import { usePureCallback } from "hooks/usePureCallback"
import { dataService } from "utils/dataService"
import { logger } from "utils/logger"
import { toRedacted } from 'martti-agent-services'

export const OUTSOURCE = "OUTSOURCE"
export const OVER_PHONE = "OPI"
export const UNKNOWN = "UNKNOWN"

export const mergeCallSegments = (current, newCs) => {
  return newCs
    ? newCs?.map((cs) => {
      const currentElement = current.find((curr) => curr.id === cs.id) || {}
      return {
        ...currentElement,
        ...cs,
        notes: cs?.notes || currentElement?.notes,
        // Same trap as notes: the notification parser defaults fullName to "", which would
        // otherwise blank a name the API supplied. Anything keyed on the name then reads that
        // interpreter as gone.
        interpreterName: cs?.interpreterName || currentElement?.interpreterName,
        agentId: cs?.agentId ?? currentElement?.agentId,
        startTime: cs?.startTime ?? currentElement?.startTime,
        endTime: cs?.endTime ?? currentElement?.endTime,
      }
    })
    : current
}

export const useCallSegments = () => {
  const { userData } = useAuth()
  const { answeredCall, expectingAnswer } = useCall()
  const { additionalUserData } = useProfile()

  const [callSegments, setCallSegments] = useState([])

  const multiPatientEnabled = useConfigFlag("multiPatient.enabled")
  const refreshIntervalSeconds = useConfigFlag("multiPatient.refreshIntervalSeconds", 10)

  const showSegment = useCallback(
    (cs) => {
      if ((cs.type || cs.callSegmentType) === UNKNOWN) {
        return false
      }
      if (cs.participantMode === "LISTEN" && userData.role !== "SUPERVISOR") {
        return false
      }
      const inHouseUser = !additionalUserData?.microCallCenter
      const inHouseSegment = !cs?.interpreter?.external && !cs?.agentMicroCallCenter
      if (inHouseUser === inHouseSegment) {
        return true
      }
      if ([OVER_PHONE, OUTSOURCE].includes(cs.type || cs.callSegmentType)) {
        return true
      }
    },
    [userData?.role, additionalUserData?.microCallCenter]
  )

  const parseAPICallSegments = useCallback(
    (response) => {
      return response?.filter?.(showSegment)?.map((cs) => {
        const {
          id,
          callId,
          name,
          notes,
          languageId,
          callSegmentType,
          agentId,
          channel,
          agentName = "",
          interpreterName = "",
          outsourcePartnerName,
          startTime,
          endTime,
        } = cs
        return {
          id,
          callId,
          name,
          notes,
          languageId,
          type: callSegmentType,
          partnerName: outsourcePartnerName,
          interpreterId: agentId || additionalUserData.id,
          interpreterName: agentName || interpreterName,
          channel,
          agentId,
          startTime,
          endTime,
        }
      })
    },
    [showSegment, additionalUserData]
  )

  const onAPICallSegmentsReceived = usePureCallback((newSegments) => {
    const parsed = parseAPICallSegments(newSegments)
    setCallSegments((current) => mergeCallSegments(current, parsed))
  })

  const fetchAPICallSegments = usePureCallback(async (connected) => {
    if (!connected || !answeredCall?.interaction) {
      return null
    }
    try {
      const response = await dataService.Interactions.CallSegments.get({ interactionId: answeredCall.interaction.id })
      logger.debug("Call Segments fetched", response?.data?.map(toRedacted))
      onAPICallSegmentsReceived(response?.data)
    } catch (err) {
      logger.error("Unable to fetch call segments", err)
    }
  })

  const parseSSECallSegments = useCallback(
    (callSegments) => {
      return callSegments?.filter?.(showSegment).map((cs) => {
        const {
          callSegmentId,
          callSegmentName,
          notes,
          language,
          type,
          channel,
          interpreter,
          fullName = "",
          interaction,
          outsourcePartner,
        } = cs
        return {
          id: callSegmentId,
          callId: interaction?.interactionId,
          name: callSegmentName,
          notes,
          languageId: language?.languageId,
          type,
          partnerName: outsourcePartner?.name,
          interpreterId: interpreter?.interpreterId || additionalUserData.id,
          interpreterName: fullName,
          channel: channel,
          agentId: interpreter?.interpreterId,
          startTime: cs.startTime,
          endTime: cs.endTime,
        }
      })
    },
    [showSegment, additionalUserData]
  )

  // The subscription carries only presence and timing -- no name, no notes. Keys it has no
  // value for are left off entirely so mergeCallSegments keeps what the API read supplied.
  const parseSubscribedCallSegments = useCallback(
    (segments) =>
      (segments ?? []).map((cs) => ({
        id: cs.id,
        agentId: cs.userId,
        interpreterId: cs.userId || additionalUserData.id,
        startTime: cs.startTime,
        endTime: cs.endTime,
      })),
    [additionalUserData]
  )

  const onSubscribedCallSegmentsReceived = usePureCallback((segments) => {
    if (!segments) return
    setCallSegments((current) => mergeCallSegments(current, parseSubscribedCallSegments(segments)))
  })

  const onSSECallSegmentsReceived = usePureCallback((newSegments) => {
    const parsed = parseSSECallSegments(newSegments)
    setCallSegments((current) => mergeCallSegments(current, parsed))
  })

  useEffect(() => {
    if (!answeredCall?.interaction?.id) {
      return
    }

    fetchAPICallSegments(true)

    SSE.on("call-segments", onSSECallSegmentsReceived)
    SSE.on("connect", fetchAPICallSegments)
    SSE.subscribe("call-segments", answeredCall?.interaction?.id)
    return () => {
      SSE.off("call-segments", onSSECallSegmentsReceived)
      SSE.unsubscribe("call-segments")
      SSE.off("connect", fetchAPICallSegments)
      setCallSegments([])
    }
  }, [answeredCall?.interaction?.id, onSSECallSegmentsReceived, fetchAPICallSegments, onAPICallSegmentsReceived])

  // Departures have no push otherwise: the SSE payload has no endTime, so an interpreter who
  // hangs up looks present until the list is read again. Subscribing means the others notice
  // within about a second instead of on the next poll, which still runs as a fallback.
  useEffect(() => {
    const interactionId = answeredCall?.interaction?.id
    if (!interactionId || !multiPatientEnabled) return undefined

    const onError = (err) => {
      logger.error(`Call segment subscription failed for interaction ${interactionId}`, err)
    }

    const unsubscribe = dataService.Interactions.CallSegments.subscribe(onSubscribedCallSegmentsReceived, {
      interactionId,
      onError,
    })

    return () => {
      if (Array.isArray(unsubscribe)) {
        unsubscribe.forEach((fn) => fn?.())
        return
      }
      unsubscribe?.()
    }
  }, [answeredCall?.interaction?.id, multiPatientEnabled, onSubscribedCallSegmentsReceived])

  const refetchedForIdentity = useRef(null)

  // Nothing else tells a session that somebody left. The notification payload carries no
  // endTime, so an interpreter who hangs up leaves their segment looking open until the list is
  // read again -- which used to happen only at call start, so the interpreter left behind stayed
  // locked out of the patient until they reloaded the page.
  useEffect(() => {
    const intervalMs = Number(refreshIntervalSeconds) * 1000
    if (!answeredCall?.interaction?.id || !multiPatientEnabled) return undefined
    if (!Number.isFinite(intervalMs) || intervalMs <= 0) return undefined

    const timer = setInterval(() => fetchAPICallSegments(true), intervalMs)
    return () => clearInterval(timer)
  }, [answeredCall?.interaction?.id, multiPatientEnabled, refreshIntervalSeconds, fetchAPICallSegments])

  useEffect(() => {
    // PI-469: At the start of calls, there is a race between the media server and the agent client/notification server
    // In some cases, by the time the request to subscribe to the call segments was received by the server, the media
    // server had already dispatched events for the first participants joining, therefore the agent client would miss
    // the call segment updated events triggered by those media server events.
    // In that case the call sheet is stuck with no call segments to display at the start of the call.
    // Here we use the expectingAnswer value from the call context, which changes when the webrtc CALL_STARTED event fires
    // If the call does not have call segments by the time the webrtc call starts, we fetch them again
    const interactionId = answeredCall?.interaction?.id
    // A segment the subscription delivered has an agent but no name -- the subscription does
    // not carry one. Under multi-patient the name is shown to the other interpreters, so a
    // missing one is worth another read; without it nothing displays a name and the extra
    // request would be waste.
    const needsIdentity = (cs) => !cs.agentId || (multiPatientEnabled && !cs.interpreterName)
    const unidentified = callSegments.filter(needsIdentity).map((cs) => cs.id).sort().join(",")
    const needsIdentityRefetch = unidentified.length > 0 && refetchedForIdentity.current !== unidentified

    if (interactionId && expectingAnswer === false && (!callSegments.length || needsIdentityRefetch)) {
      if (needsIdentityRefetch) {
        refetchedForIdentity.current = unidentified
      }
      dataService.Interactions.CallSegments.get({ interactionId: answeredCall.interaction.id })
        .then((response) => {
          logger.debug("Call Segments fetched", response?.data?.map(toRedacted))
          if (!response?.data?.length) {
            return
          }
          onAPICallSegmentsReceived(response?.data)
        })
        .catch((err) => {
          logger.error("Unable to fetch call segments", err)
        })
    }

  }, [callSegments, expectingAnswer, answeredCall?.interaction?.id, multiPatientEnabled, onAPICallSegmentsReceived]);

  return {
    callSegments,
  }
}
